import { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { GET_CATEGORIES } from "../../constant";
import { DataGrid } from "../index";
import { start, done } from "../../store/loaderSlice";
import { snackbarStart } from "../../store/SnackbarSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CategoryIcon from "@mui/icons-material/Category";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const DashboardCategories = () => {
  const { token } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [path, setPath] = useState("");
  const [url, setUrl] = useState("");

  const fetchCategories = async () => {
    dispatch(start());
    try {
      const { data } = await axios.get(GET_CATEGORIES, {
        headers: { "x-auth-token": token },
      });
      setCategories(data);
    } catch (err) {
      console.error(err);
      navigate("/");
    } finally {
      dispatch(done());
    }
  };

  const handleDeleteCategories = async () => {
    if (!selectedIds.length) return;
    dispatch(start());
    try {
      await axios.delete(GET_CATEGORIES, {
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        data: { ids: selectedIds },
      });
      dispatch(
        snackbarStart({
          text: t("categoriesDashboard.deletedMessage"),
          severity: "success",
        }),
      );
      fetchCategories();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
      setSelectedIds([]);
    }
  };

  const validateFields = () => {
    if (
      name.trim().length < 2 ||
      path.trim().length < 2 ||
      url.trim().length < 2
    ) {
      dispatch(
        snackbarStart({
          text: t("categoriesDashboard.fieldsTwoCharacter"),
          severity: "error",
        }),
      );
      return false;
    }
    return true;
  };

  const handleCreateCategory = async () => {
    if (!validateFields()) return;
    dispatch(start());
    try {
      await axios.post(
        GET_CATEGORIES,
        { name, path, url },
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token,
          },
        },
      );
      dispatch(
        snackbarStart({
          text: t("categoriesDashboard.createdMessage"),
          severity: "success",
        }),
      );
      handleCancel();
      fetchCategories();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
    }
  };

  const handleUpdateCategory = async () => {
    if (!validateFields()) return;
    dispatch(start());
    try {
      await axios.put(
        `${GET_CATEGORIES}/${selectedIds[0]}`,
        { name, path, url },
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token,
          },
        },
      );
      dispatch(
        snackbarStart({
          text: t("categoriesDashboard.editedMessage"),
          severity: "success",
        }),
      );
      handleCancel();
      fetchCategories();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
    }
  };

  const handleAddCategory = () => {
    setIsEditing(false);
    setName("");
    setPath("");
    setUrl("");
    setShowForm(true);
  };

  const handleEditCategory = () => {
    if (selectedIds.length !== 1) {
      dispatch(
        snackbarStart({
          text: t("selectOneItem") || "Select exactly one item",
          severity: "warning",
        }),
      );
      return;
    }
    const target = categories.find((c) => c._id === selectedIds[0]);
    setName(target?.name || "");
    setPath(target?.path || "");
    setUrl(target?.url || "");
    setIsEditing(true);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setName("");
    setPath("");
    setUrl("");
    setSelectedIds([]);
  };

  const onSelectionChange = (ids) => setSelectedIds(ids);

  const columns = [
    { field: "id", headerName: "ID", width: 220 },
    { field: "name", headerName: t("categoriesDashboard.name"), width: 200 },
    { field: "path", headerName: t("categoriesDashboard.path"), width: 200 },
    {
      field: "url",
      headerName: t("categoriesDashboard.url") || "URL",
      width: 200,
    },
  ];

  const rows = categories.map((c) => ({
    id: c._id,
    name: c.name,
    path: c.path,
    url: c.url,
  }));

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <section className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#030712] transition-colors duration-300">
      <div className="container mx-auto px-4">
        {/* Page header */}
        <div className="flex items-center gap-4 mb-8 mt-2">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-500 flex-shrink-0">
            <CategoryIcon sx={{ fontSize: 26 }} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">
              {t("dashboard.categories")}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {categories.length} {t("total") || "total records"}
            </p>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="flex flex-wrap gap-2.5 mb-5">
          <button
            onClick={handleAddCategory}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <AddIcon sx={{ fontSize: 17 }} />{" "}
            {t("categoriesDashboard.newCategory")}
          </button>

          <button
            onClick={handleEditCategory}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <EditIcon sx={{ fontSize: 17 }} />{" "}
            {t("categoriesDashboard.editCategory")}
          </button>

          <button
            onClick={handleDeleteCategories}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <DeleteIcon sx={{ fontSize: 17 }} /> {t("delete")}
          </button>
        </div>

        {/* Data grid */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0f172a] shadow-sm mb-6">
          <DataGrid
            rows={rows}
            columns={columns}
            title=""
            checkboxSelection
            selectedIds={selectedIds}
            sx={{
              border: "none",
              "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
            onRowsSelectionHandler={onSelectionChange}
          />
        </div>

        {/* Inline form */}
        {showForm && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0f172a] shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-5">
              {isEditing
                ? t("categoriesDashboard.editCategory")
                : t("categoriesDashboard.newCategory")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                {
                  value: name,
                  setter: setName,
                  placeholder: t("categoriesDashboard.name"),
                },
                {
                  value: path,
                  setter: setPath,
                  placeholder: t("categoriesDashboard.path"),
                },
                {
                  value: url,
                  setter: setUrl,
                  placeholder: t("categoriesDashboard.url") || "URL",
                },
              ].map(({ value, setter, placeholder }, i) => (
                <input
                  key={i}
                  type="text"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  placeholder={placeholder}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all"
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={
                  isEditing ? handleUpdateCategory : handleCreateCategory
                }
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-all"
              >
                <CheckIcon sx={{ fontSize: 17 }} />
                {isEditing ? t("update") : t("create")}
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-semibold transition-all"
              >
                <CloseIcon sx={{ fontSize: 17 }} /> {t("cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardCategories;
